import { computed, inject, Injectable, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { AuthService } from '@app/core/auth/auth.service';
import { BillingStore } from '@app/core/billing/billing.store';
import {
  AutomaticExportRequest,
  EdiKey,
  PricePolicyType,
  toPricePolicyType,
  UpdateChargeAssignSettingsRequest,
  UpdateMileageSettingsRequest,
} from '@app/core/driver/driver.models';
import { DriverStore } from '@app/core/driver/driver.store';
import { IManagerRow } from '@app/core/driver/settings/settings.models';
import { ErrorHandler } from '@app/core/handler/error.handler';
import { IamStore } from '@app/core/iam/iam.store';
import { UserStore } from '@app/core/user/user.store';
import { ChargeRepayErrorCodes } from '@app/shared/errors/error.codes';
import { CRError } from '@app/shared/models/error.models';
import { TranslateService } from '@ngx-translate/core';
import {
  catchError,
  concatMap,
  forkJoin,
  from,
  of,
  switchMap,
  tap,
  throwError,
  toArray,
} from 'rxjs';
import { DriverSettingsEkvDialog } from './driver-settings-ekv/driver-settings-dialog/driver-settings-ekv-dialog.component';

@Injectable({
  providedIn: 'root',
})
export class DriverSettingsService {
  private readonly authService: AuthService = inject(AuthService);
  private readonly driverStore: DriverStore = inject(DriverStore);
  private readonly iamStore: IamStore = inject(IamStore);
  private readonly billingStore: BillingStore = inject(BillingStore);
  private readonly userStore: UserStore = inject(UserStore);
  private readonly trxService: TranslateService = inject(TranslateService);
  private readonly errorHandler = inject(ErrorHandler);
  private readonly ekv = inject(MatDialog);

  public readonly selectedSettingsItem = signal<string>('administrators');
  public readonly updatingCalibrationLaw = signal<boolean>(false);
  public readonly updatingPaymentProvider = signal<boolean>(false);
  public readonly updatingMileageRequest = signal<boolean>(false);
  public readonly updatingMileageSettings = signal<boolean>(false);
  public readonly updatingPricePolicy = signal<boolean>(false);
  public readonly updatingExternalBackendProvider = signal<boolean>(false);
  public readonly updatingBackendProvider = signal<boolean>(false);
  public readonly updatingRfidCharging = signal<boolean>(false);
  public readonly updatingAutomaticCompanyCar = signal<boolean>(false);
  public readonly updatingExportReceivers = signal<boolean>(false);
  public readonly deletingMember = signal<boolean>(false);
  public readonly invitingMember = signal<boolean>(false);
  public readonly upgradingSubscription = signal<boolean>(false);
  public readonly updatingAutoExport = signal<boolean>(false);
  public readonly updatingAutoExportSettings = signal<boolean>(false);
  public readonly deletingReceiver = signal<boolean>(false);
  public readonly updatingChargingSettings = signal<boolean>(false);
  public readonly updatingProfile = signal<boolean>(false);

  public readonly userFirstName = computed(() => this.userStore.userProfile()?.firstName ?? '');
  public readonly userLastName = computed(() => this.userStore.userProfile()?.lastName ?? '');
  public readonly userEmail = computed(() => this.userStore.userProfile()?.email ?? '');
  public readonly userFullName = computed(() => {
    const profile = this.userStore.userProfile();
    return profile ? `${profile.firstName} ${profile.lastName}` : '';
  });

  public readonly driverOrganization = computed(() => this.driverStore.organization());
  public readonly managers = computed(() => this.iamStore.managers());
  public readonly invitations = computed(() => this.iamStore.invitations());
  public readonly subscription = computed(() => this.billingStore.subscription());

  public readonly exportReceivers = computed(
    () => this.driverOrganization()?.automatic_export_emails || [],
  );

  public readonly autoExportAvailable = computed(
    () => this.driverOrganization()?.automatic_export_activated,
  );

  public readonly eichrechtChargingEnabled = computed(
    () => this.driverOrganization()?.eichrecht_charging || false,
  );

  public readonly reimbursementEnabled = computed(
    () => this.driverOrganization()?.reimbursement_activated || false,
  );

  public readonly externalBackendProviderEnabled = computed(
    () => this.driverOrganization()?.backend_provider_driver_idtag || false,
  );

  public readonly rfidChargingEnabled = computed(
    () => this.driverOrganization()?.direct_ocpp_idtag_assign_charges || false,
  );

  public readonly automaticCompanyCarEnabled = computed(
    () => this.driverOrganization()?.charge_assign_allow_automatic_company_car || false,
  );

  public readonly mileageRequestEnabled = computed(
    () => this.driverOrganization()?.mileage_request_activated || false,
  );

  public readonly mileageEmailAfterDays = computed(
    () => this.driverOrganization()?.mileage_email_after_days || 0,
  );

  public readonly mileageOutdatedDays = computed(
    () => this.driverOrganization()?.mileage_outdated_days || 0,
  );

  public readonly chargeAssignEmailTimeSeconds = computed(
    () => this.driverOrganization()?.charge_assign_email_time_seconds || 0,
  );

  public readonly chargeAssignTimeSeconds = computed(
    () => this.driverOrganization()?.charge_assign_time_seconds || 0,
  );

  public readonly ediKeys = computed(() => this.driverStore.organizationKeys());

  /** Active price policy for the current calendar year. Upcoming years are ignored. */
  public readonly currentPricePolicyType = computed((): PricePolicyType | null => {
    const active = this.driverOrganization()?.price_policy?.active;
    if (!active || active.year !== new Date().getFullYear()) {
      return null;
    }

    return toPricePolicyType(active.price_policy);
  });

  /** Price policy already chosen for the next calendar year, if any. */
  public readonly upcomingPricePolicyType = computed((): PricePolicyType | null => {
    const upcoming = this.driverOrganization()?.price_policy?.upcoming;
    if (!upcoming || upcoming.year !== new Date().getFullYear() + 1) {
      return null;
    }

    return toPricePolicyType(upcoming.price_policy);
  });

  // Current-year flat rate in cents/kWh; the API stores euros/kWh.
  public readonly currentYearFlatrateCents = computed((): number | null => {
    const prices = this.driverStore.flatratePrices();
    if (!prices) {
      return null;
    }

    const year = new Date().getFullYear();
    const price =
      prices.find((entry) => entry.year === year && entry.active) ??
      prices.find((entry) => entry.year === year);

    if (!price) {
      return null;
    }

    return Math.round(price.value * 10000) / 100;
  });

  // Next-year flat rate in cents/kWh; match on year only because it is not active yet.
  public readonly upcomingYearFlatrateCents = computed((): number | null => {
    const prices = this.driverStore.flatratePrices();
    if (!prices) {
      return null;
    }

    const year = new Date().getFullYear() + 1;
    const price =
      prices.find((entry) => entry.year === year && entry.active) ??
      prices.find((entry) => entry.year === year);

    if (!price) {
      return null;
    }

    return Math.round(price.value * 10000) / 100;
  });

  // Prefer local storage, then the active language, then the TranslateService fallback.
  public getInitialLang(): string {
    const saved = localStorage.getItem('locale');
    const current = this.trxService.getCurrentLang();
    const def = this.trxService.getFallbackLang();

    return (saved || current || def || 'en-EN') as string;
  }

  public saveUpcomingPricePolicy(pricePolicy: PricePolicyType, informDriversByEmail: boolean) {
    this.updatingPricePolicy.set(true);

    const create$ = this.driverStore.createPricePolicy({
      inform_drivers_by_email: informDriversByEmail,
      price_policy: pricePolicy,
      year: new Date().getFullYear() + 1,
    });
    const hasUpcoming = this.upcomingPricePolicyType() !== null;
    const save$ = hasUpcoming
      ? this.driverStore.deletePricePolicy().pipe(concatMap(() => create$))
      : create$;

    save$.subscribe({
      next: () => this.updatingPricePolicy.set(false),
      error: (error: CRError) => {
        this.updatingPricePolicy.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public loadFlatratePrices() {
    this.driverStore.loadFlatratePrices().subscribe({
      error: (error: CRError) => this.errorHandler.handleError(error),
    });
  }

  public getLanguageLabel(code: string): string {
    const fromI18n = this.trxService.instant(`languages.${code}`);
    if (fromI18n) return fromI18n;
    return 'no-lang';
  }

  public addExportReceiver(email: string) {
    this.updatingExportReceivers.set(true);

    if (this.exportReceivers().includes(email)) {
      const alreadyExistsError: CRError = {
        errorCode: ChargeRepayErrorCodes.EXPORT_RECEIVER_EXISTS_ERROR,
        severity: 'error',
      };
      this.errorHandler.handleError(alreadyExistsError);

      this.updatingExportReceivers.set(false);

      return;
    }

    const updatedReceivers = [...this.exportReceivers(), email];

    this.driverStore.updateExportReceivers(updatedReceivers).subscribe({
      next: () => {
        this.updatingExportReceivers.set(false);
      },
      error: (error: CRError) => {
        this.updatingExportReceivers.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public removeExportReceiver(email: string) {
    this.deletingReceiver.set(true);

    const updatedReceivers = this.exportReceivers().filter((e) => e !== email);

    this.driverStore.updateExportReceivers(updatedReceivers).subscribe({
      next: () => {
        this.deletingReceiver.set(false);
      },
      error: () => {
        // Not using the generic settings update failed error but replace it with deletion error
        this.deletingReceiver.set(false);

        const deleteReceiverError: CRError = {
          errorCode: ChargeRepayErrorCodes.DELETE_RECEIVER_ERROR,
          severity: 'error',
        };
        this.errorHandler.handleError(deleteReceiverError);
      },
    });
  }

  public addEdiKey(key: string, userWritable: boolean, regularExpression: string) {
    if (this.driverStore.organizationKeys().some((k) => k.key === key)) {
      const alreadyExistsError: CRError = {
        errorCode: ChargeRepayErrorCodes.EDI_KEY_EXISTS_ERROR,
        severity: 'error',
      };
      this.errorHandler.handleError(alreadyExistsError);
      return of(null);
    }

    const ediKey: EdiKey = {
      key,
      user_writable: userWritable,
      regular_expression: regularExpression,
    };

    return this.driverStore.addEdi(ediKey).pipe(
      catchError((err) => {
        this.errorHandler.handleError(err);
        return of(null);
      }),
    );
  }

  // Remove keys one after another so one failure does not abort the rest.
  public removeEdiKeys(keys: string[], force: boolean = false) {
    console.log('Removing EDI keys:', keys, 'with force:', force);
    return from(keys).pipe(
      concatMap((key) =>
        this.driverStore.removeEdi(key, force).pipe(
          catchError((err: CRError) => {
            if (err.errorCode === ChargeRepayErrorCodes.EDI_KEY_STILL_IN_USE) {
              const dialogRef = this.ekv.open(DriverSettingsEkvDialog, {
                data: { key },
              });
              return dialogRef.afterClosed();
            } else {
              this.errorHandler.handleError(err);
              return of(null);
            }
          }),
        ),
      ),
      // helper method which collects all emmited values from the dialog (in case of multiple dialogs are opened) in an array
      // this avoids completion of the calling component which shows a loading spinner on the deletion button
      toArray(),
    );
  }

  public updateCalibrationLaw(enabled: boolean) {
    console.debug('Updating calibration law to:', enabled);

    this.updatingCalibrationLaw.set(true);

    this.driverStore.updateCalibrationLaw(enabled).subscribe({
      next: () => {
        this.updatingCalibrationLaw.set(false);
      },
      error: (error: CRError) => {
        this.updatingCalibrationLaw.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateAutoExport(enabled: boolean) {
    console.debug('Updating auto export to:', enabled);
    this.updatingAutoExport.set(true);

    const currentDay = this.driverOrganization()?.automatic_export_day_of_month;

    // If there is a valid day already (> 0), don't provide the second argument.
    // If it's 0, null, or undefined, provide the default value '1'.
    const update$ =
      currentDay && currentDay > 0
        ? this.driverStore.updateAutoExport(enabled)
        : this.driverStore.updateAutoExport(enabled, 1);

    update$.subscribe({
      next: () => {
        this.updatingAutoExport.set(false);
      },
      error: (error: CRError) => {
        this.updatingAutoExport.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateAutoExportSettings(dayOfMonth: number, language: string) {
    this.updatingAutoExportSettings.set(true);

    const autoExportRequest: AutomaticExportRequest = {
      automatic_export_day_of_month: dayOfMonth,
      automatic_export_locale: language,
    };

    this.driverStore.updateAutoExportSettings(autoExportRequest).subscribe({
      next: () => {
        this.updatingAutoExportSettings.set(false);
      },
      error: (error: CRError) => {
        this.updatingAutoExportSettings.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateRfidCharging(enabled: boolean) {
    console.debug('Updating RFID charging to:', enabled);

    this.updatingRfidCharging.set(true);

    this.driverStore.updateRfidCharging(enabled).subscribe({
      next: () => {
        this.updatingRfidCharging.set(false);
      },
      error: (error: CRError) => {
        this.updatingRfidCharging.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateAutomaticCompanyCar(enabled: boolean) {
    console.debug('Updating automatic company car to:', enabled);

    this.updatingAutomaticCompanyCar.set(true);

    this.driverStore.updateAutomaticCompanyCar(enabled).subscribe({
      next: () => {
        this.updatingAutomaticCompanyCar.set(false);
      },
      error: (error: CRError) => {
        this.updatingAutomaticCompanyCar.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updatePaymentProvider(enabled: boolean) {
    console.debug('Updating payment provider to:', enabled);

    this.updatingPaymentProvider.set(true);

    this.driverStore.updatePaymentProvider(enabled).subscribe({
      next: () => {
        this.updatingPaymentProvider.set(false);
      },
      error: (error: CRError) => {
        this.updatingPaymentProvider.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateMileageRequest(enabled: boolean) {
    console.debug('Updating mileage request to:', enabled);

    this.updatingMileageRequest.set(true);

    this.driverStore.updateMileageRequest(enabled).subscribe({
      next: () => {
        this.updatingMileageRequest.set(false);
      },
      error: (error: CRError) => {
        this.updatingMileageRequest.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateMileageSettings(emailAfterDays: number, outdatedDays: number) {
    this.updatingMileageSettings.set(true);

    const request: UpdateMileageSettingsRequest = {
      mileage_email_after_days: emailAfterDays,
      mileage_outdated_days: outdatedDays,
    };

    this.driverStore.updateMileageSettings(request).subscribe({
      next: () => {
        this.updatingMileageSettings.set(false);
      },
      error: (error: CRError) => {
        this.updatingMileageSettings.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public updateExternalBackendProvider(enabled: boolean) {
    console.debug('Updating external backend provider to:', enabled);

    this.driverStore.updateExternalBackendProvider(enabled).subscribe({
      next: () => {
        this.updatingExternalBackendProvider.set(false);
      },
      error: (error: CRError) => {
        this.updatingExternalBackendProvider.set(false);

        this.errorHandler.handleError(error);
      },
    });
  }

  public updateBackendProvider(provider_name: string, provider_url: string) {
    console.debug('Updating backend provider with name:', provider_name, 'url:', provider_url);

    this.updatingBackendProvider.set(true);

    const payload = { provider_name, provider_url };
    const request$ = this.driverStore.backendProvider()
      ? this.driverStore.updateBackendProvider(payload)
      : this.driverStore.createBackendProvider(payload);

    request$.subscribe({
      next: () => {
        this.updatingBackendProvider.set(false);
      },
      error: (error: CRError) => {
        this.updatingBackendProvider.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public deleteMember(member: IManagerRow) {
    if (!member.id) {
      console.warn('Cannot delete member without id');
      return;
    }

    console.debug('Deleting member:', member);

    this.deletingMember.set(true);

    this.iamStore.deleteManager(member.id, member.invitation ?? false).subscribe({
      next: () => {
        this.deletingMember.set(false);
      },
      error: (error: CRError) => {
        this.deletingMember.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public inviteMember(email: string, locale: string) {
    if (!email || !locale) {
      console.warn('Cannot invite member without email or locale');
      return;
    }

    console.debug('Inviting member with email:', email, 'and locale:', locale);

    this.invitingMember.set(true);

    this.iamStore.inviteManager(email, locale).subscribe({
      next: () => {
        this.invitingMember.set(false);
      },
      error: (error: CRError) => {
        this.invitingMember.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }

  public upgradeSubscription(amount: number) {
    this.upgradingSubscription.set(true);

    return this.billingStore.upgradeSubscription(this.subscription()!.id, amount).pipe(
      tap(() => {
        this.upgradingSubscription.set(false);
      }),
    );
  }

  public updateProfile(firstName: string, lastName: string, language: string) {
    this.updatingProfile.set(true);

    return forkJoin([
      this.iamStore.updateProfile(firstName, lastName),
      this.iamStore.updateLanguage(language),
    ]).pipe(
      switchMap(() =>
        from(this.authService.refreshToken(true)).pipe(
          catchError(() =>
            throwError(
              () =>
                ({
                  errorCode: ChargeRepayErrorCodes.REFRESH_TOKEN_ERROR,
                  severity: 'panic',
                }) as CRError,
            ),
          ),
        ),
      ),
      switchMap(() => this.userStore.reinitialize()),
      tap(() => {
        this.updatingProfile.set(false);
      }),
      catchError((error: CRError) => {
        this.updatingProfile.set(false);
        this.errorHandler.handleError(error);
        return throwError(() => error);
      }),
    );
  }

  public updateChargeAssignSettings(emailReminderDays: number, assignmentTimeoutDays: number) {
    const emailTimeSeconds = emailReminderDays * 86400;
    const assignTimeSeconds = assignmentTimeoutDays * 86400;

    this.updatingChargingSettings.set(true);

    const request: UpdateChargeAssignSettingsRequest = {
      charge_assign_email_time_seconds: emailTimeSeconds,
      charge_assign_time_seconds: assignTimeSeconds,
    };

    this.driverStore.updateChargeAssignSettings(request).subscribe({
      next: () => {
        this.updatingChargingSettings.set(false);
      },
      error: (error: CRError) => {
        this.updatingChargingSettings.set(false);
        this.errorHandler.handleError(error);
      },
    });
  }
}
